/*
  Revives the carousels inside BAKED (converted) Replo sections.

  A stopgap for the sections not yet rebuilt natively. Those carry Replo's own markup — a track
  paged by `transform: translate3d(...)`, dots as `div[role="button"][aria-label="Go to slide N"]`,
  arrows as `button[data-replo-part="nav-button"][data-direction]`, and a `.replo-sr-only` live
  region — and the conversion stripped the runtime that moved them. The result is a control that
  looks clickable, announces itself to a screen reader, and does nothing.

  Rebuilt sections use <pdp-carousel> (assets/pdp-carousel.js), which is scroll-snap based and
  needs no script for the gesture. Delete this file when the last baked section is replaced.

  ── Pairing controls to tracks ────────────────────────────────────────────────────────
  Three traps, all of them recorded in REPLO-CONVERSION-HANDOVER.md and all of them silent:

  1. `data-replo-root-id` CANNOT be the key. It is null on every dot and arrow; only the track
     carries it. Pairing has to be structural.
  2. The controls are NOT inside the element that wraps the track. Replo nests a
     `[data-replo-component-root="carousel"]` viewport around the track and puts the dots and
     arrows in an OUTER wrapper. Resolving to the nearer of the two finds a subtree with no
     controls and reports a working carousel as static.
  3. ONE ROOT CAN HOLD SEVERAL TRACKS — AIR PRO s3 has six. So "all the controls in this root
     drive the one track in it" is wrong there, and scoping by root skips those carousels
     entirely.

  So this walks UP FROM EACH CONTROL instead: the first ancestor that contains any track owns
  that control. If that ancestor contains more than one track the pairing is genuinely ambiguous
  and the control is left alone and logged — a carousel that does nothing is better than one that
  pages a different carousel.

  ── Why the `disabled` attribute matters ──────────────────────────────────────────────
  Replo froze `disabled=""` onto whichever arrows were unusable at capture time, and a disabled
  <button> never fires a click. Clearing it is a precondition for the arrow working at all.
*/

(function () {
  const TRACK = '[data-replo-part="slide-track"]';
  const tracks = [...document.querySelectorAll(TRACK)];
  if (!tracks.length) return;

  // control -> its track, by climbing to the first ancestor that contains any track
  const ownerTrack = (el) => {
    let node = el.parentElement;
    while (node) {
      const found = node.querySelectorAll(TRACK);
      if (found.length === 1) return found[0];
      if (found.length > 1) return null; // ambiguous
      node = node.parentElement;
    }
    return null;
  };

  // track -> {go, slides}, so a label strip discovered below can page the track it belongs to.
  const pagers = new Map();

  const groups = new Map(); // track -> {dots:[], prev, next, status}
  const groupFor = (track) => {
    if (!groups.has(track)) groups.set(track, { dots: [], prev: null, next: null, status: null });
    return groups.get(track);
  };

  let ambiguous = 0;

  document.querySelectorAll('[aria-label^="Go to slide"]').forEach((dot) => {
    const t = ownerTrack(dot);
    if (!t) { ambiguous++; return; }
    groupFor(t).dots.push(dot);
  });

  document.querySelectorAll('[data-replo-part="nav-button"][data-direction]').forEach((btn) => {
    const t = ownerTrack(btn);
    if (!t) { ambiguous++; return; }
    const g = groupFor(t);
    if (btn.getAttribute('data-direction') === 'previous') g.prev = g.prev || btn;
    else g.next = g.next || btn;
  });

  if (ambiguous) {
    console.warn('[replo-baked-carousel] %d control(s) could not be paired to a single track', ambiguous);
  }

  groups.forEach((g, track) => {
    // Clones are Replo's loop padding — counting them makes the dots and the live region
    // disagree with the dots that actually exist.
    const slides = [...track.children].filter(
      (el) => el.matches('[data-replo-part="slide"]') && el.getAttribute('data-is-cloned') !== 'true',
    );
    if (slides.length < 2) return;

    // The live region sits with the controls, not the track, so find it the same way.
    let node = track.parentElement;
    while (node && !g.status) {
      g.status = node.querySelector('.replo-sr-only[aria-live], [aria-live="polite"]');
      node = node.parentElement;
    }

    // The active-dot look is a generated atomic class and cannot be hard-coded. Derive it:
    // whichever classes the initially-current dot has that its siblings do not.
    const current = g.dots.find((d) => d.getAttribute('aria-current') === 'true') || g.dots[0];
    const activeClasses = current
      ? [...current.classList].filter((c) => g.dots.every((o) => o === current || !o.classList.contains(c)))
      : [];

    let index = 0;

    const setEnabled = (el, on) => {
      if (!el) return;
      if (on) el.removeAttribute('disabled');
      else el.setAttribute('disabled', '');
      el.setAttribute('aria-disabled', on ? 'false' : 'true');
    };

    const step = () =>
      slides.length > 1
        ? slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left
        : track.getBoundingClientRect().width;

    const go = (i) => {
      index = Math.max(0, Math.min(i, slides.length - 1));
      // Replo bakes the transition as `none`/`0ms`, so a move would jump. Set it here rather than
      // in CSS: the generated stylesheet is regenerated by the pipeline and would lose it.
      track.style.transitionProperty = 'transform';
      track.style.transitionDuration =
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ? '0ms' : '350ms';
      track.style.transform = `translate3d(${-index * step()}px, 0px, 0px)`;

      g.dots.forEach((d, n) => {
        d.setAttribute('aria-current', n === index ? 'true' : 'false');
        activeClasses.forEach((c) => d.classList.toggle(c, n === index));
      });
      if (g.status) g.status.textContent = `Slide ${index + 1} of ${slides.length}`;
      setEnabled(g.prev, index > 0);
      setEnabled(g.next, index < slides.length - 1);
    };

    pagers.set(track, { go, slides });

    g.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => go(i));
      // Already `role="button"` and `tabindex="0"`, so they take focus and must answer the
      // keyboard too — otherwise they are only half a control.
      dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(i); }
      });
    });
    g.prev?.addEventListener('click', () => go(index - 1));
    g.next?.addEventListener('click', () => go(index + 1));

    // Seat the initial state so a frozen `disabled` is cleared before the first click.
    setEnabled(g.prev, false);
    setEnabled(g.next, slides.length > 1);
  });

  /*
    ── Linked carousels: the attachment picker ───────────────────────────────────────────

    The attachment picker on AIR and AIR PRO s3 is not a select and not a set of dots. It is
    FIVE carousels that Replo's runtime moved in lockstep: the names, the heading and
    description, the impact level with its muscle-group list, and on some pages a second
    (mobile) copy of the names. Clicking a name paged all of them. The conversion stripped the
    runtime, so the names still compute `cursor: pointer` and every track stayed frozen at
    whatever `translate3d` it was captured with. Clicking did nothing — on live too.

    What makes the link recoverable is `data-slide-source-index`. Every slide carries it and the
    numbering is SHARED across the picker's tracks: on AIR PRO 0 is Flat Metal, 1 Fork, 2 Round,
    3 Bullet, 4 Air Lock, 5 Wedge. So a click does not need to compute a position — it names a
    source index, and each track independently seeks its own slide carrying that index.

    Three things to know before touching this:

    1. `data-is-cloned` is never set here. Replo baked its loop padding as ordinary slides, so
       the same source index appears two or three times per track and index arithmetic on DOM
       position is meaningless. Seek by source index, and prefer the occurrence nearest the
       current position so the strip does not jump the length of the track.
    2. The strip has MORE slides than the panels (19 names against 8 panels on AIR PRO, because
       the names repeat three times). An earlier version of this required the panel track to be
       at least as long as the strip and silently paired nothing.
    3. Not every track is part of the picker. The same section holds two 3-slide comparison
       carousels whose source indices are 0-2, which overlap the picker's. They are excluded by
       requiring a track's DISTINCT source-index count to be within one of the strip's — the
       picker's tracks are the same authored list (6 attachments, or 5 where live is missing
       one), the comparison tables are not.

    Where a track has no slide for the requested index — the impact-level carousel on AIR PRO
    has five entries for six attachments — that track is left where it is rather than guessing.
  */
  // Takes an element OR a string: the panel finder compares whole slides, the strip compares
  // its own labels, and an earlier version of this passed elements to a string function.
  const norm = (v) =>
    ((v && v.nodeType === 1 ? v.textContent : v) || '').replace(/\s+/g, ' ').trim();
  const kids = (t) => [...t.children].filter((el) => el.matches('[data-replo-part="slide"]'));
  const srcOf = (el) => Number(el.getAttribute('data-slide-source-index'));
  const distinctSrc = (t) => new Set(kids(t).map(srcOf).filter((n) => !Number.isNaN(n))).size;
  const strideOf = (t) => {
    const k = kids(t);
    return k.length > 1 ? k[1].getBoundingClientRect().left - k[0].getBoundingClientRect().left : 0;
  };
  const positionOf = (t) => {
    const stride = strideOf(t);
    if (!stride) return 0;
    const m = new DOMMatrixReadOnly(getComputedStyle(t).transform);
    return Math.round(-m.m41 / stride);
  };
  const seek = (t, src) => {
    const k = kids(t);
    const here = positionOf(t);
    let best = -1;
    k.forEach((el, i) => {
      if (srcOf(el) !== src) return;
      if (best < 0 || Math.abs(i - here) < Math.abs(best - here)) best = i;
    });
    if (best < 0) {
      /*
        No slide for this index. AIR PRO's impact-level carousel is authored with five entries
        for six attachments — Wedge has none — so leaving the track where it is would show
        Bullet's "Impact Level: 5/5 Feet Palms Glutes Neck" underneath the Wedge panel. A gap is
        wrong; stale data that reads as fact is worse. Hide it until an index it has is chosen,
        and note the missing entry so it can be authored.
      */
      t.style.visibility = 'hidden';
      return false;
    }
    t.style.visibility = '';
    const stride = strideOf(t);
    if (!stride) return false;
    t.style.transitionProperty = 'transform';
    t.style.transitionDuration =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? '0ms' : '350ms';
    t.style.transform = `translate3d(${-best * stride}px, 0px, 0px)`;
    return true;
  };

  let linked = 0;
  tracks.forEach((strip) => {
    const labels = kids(strip);
    const texts = labels.map(norm);
    if (labels.length < 3) return;
    if (texts.some((t) => !t || t.length > 24)) return; // a strip of names, not of panels
    if (labels.some((el) => Number.isNaN(srcOf(el)))) return;

    const section = strip.closest('.shopify-section') || document;
    const stripDistinct = distinctSrc(strip);

    // The panel track: same section, longer text, and a slide for every name in the strip.
    const panel = [...section.querySelectorAll(TRACK)].find((t) => {
      if (t === strip) return false;
      const k = kids(t);
      if (k.length < 3 || Math.abs(distinctSrc(t) - stripDistinct) > 1) return false;
      if (k.every((el) => norm(el).length <= 24)) return false; // another name strip, not panels
      return texts.every((label) => {
        const key = label.toLowerCase().replace(/[^a-z0-9]/g, '');
        return k.some((el) => norm(el).toLowerCase().replace(/[^a-z0-9]/g, '').startsWith(key));
      });
    });
    if (!panel) return;

    // Everything else in the section on the same authored list moves with it.
    const followers = [...section.querySelectorAll(TRACK)].filter(
      (t) => t !== strip && kids(t).length >= 3 && Math.abs(distinctSrc(t) - stripDistinct) <= 1,
    );

    /*
      The selected name wears a black pill: `background: #000`, `border-radius: 100px`, white
      text, applied by a generated atomic class on the label's inner box. Replo moved that class
      as the selection changed; the conversion baked it onto whichever name was selected at
      capture time, so the pill sat on "Flat Metal" for ever.

      The class names cannot be hard-coded — they are per-element and regenerate — and copying
      them between labels drags each label's own width and padding along with the pill. So read
      the two states out of the DOM once (the pill from the label that has it, the resting state
      from one that does not) and apply them inline. Inline also beats the baked class, which is
      what lets the original label lose the pill when something else is chosen.
    */
    /*
      The pill sits on the box that DIRECTLY wraps the text, not on the label's outermost div.
      That distinction matters: the outer divs inherit the section's `color: #1a1a1a`, so reading
      the resting state from one of them and painting it back turned every unselected name
      near-black on a black background. Resolve the host the same way for reading and writing —
      the parent of the text node's element.
    */
    const hostOf = (slide) => {
      const text = slide.querySelector('span, p');
      const host = text && text.parentElement !== slide ? text.parentElement : null;
      return host || slide;
    };
    const hasPill = (el) => {
      const cs = getComputedStyle(el);
      return cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && parseFloat(cs.borderTopLeftRadius) > 8;
    };
    const readState = (el) => {
      const cs = getComputedStyle(el);
      return { backgroundColor: cs.backgroundColor, borderRadius: cs.borderRadius, color: cs.color };
    };
    const hosts = labels.map(hostOf);
    const onIdx = hosts.findIndex(hasPill);
    const offIdx = hosts.findIndex((el) => !hasPill(el));
    const onStyle = onIdx >= 0 ? readState(hosts[onIdx]) : null;
    const offStyle = offIdx >= 0 ? readState(hosts[offIdx]) : null;
    // Without both states there is nothing to swap between, so leave the DOM alone rather than
    // inventing a look.
    const canPaint = !!(onStyle && offStyle);

    const paint = (src) =>
      labels.forEach((slide, i) => {
        const on = srcOf(slide) === src;
        slide.setAttribute('aria-current', on ? 'true' : 'false');
        if (!canPaint) return;
        Object.assign(hosts[i].style, on ? onStyle : offStyle);
        // Replo also sets the colour on the text node itself, which would win over the host.
        slide.querySelectorAll('p, span').forEach((el) => {
          if (!el.children.length) el.style.color = (on ? onStyle : offStyle).color;
        });
      });

    labels.forEach((slide) => {
      const src = srcOf(slide);
      slide.style.cursor = 'pointer';
      slide.setAttribute('role', 'button');
      if (!slide.hasAttribute('tabindex')) slide.setAttribute('tabindex', '0');
      const activate = () => { followers.forEach((t) => seek(t, src)); paint(src); };
      slide.addEventListener('click', activate);
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });

    /*
      Seat everything on load, and re-seat on resize.

      Both are necessary, and the second is the one that shows. The frozen transform is whatever
      the page was captured at — a DESKTOP offset, in units of the desktop slide width. At 375
      the slides are narrower, so that same offset lands mid-slide and two panels appear side by
      side with their text sliced down the middle. Nothing in the conversion re-seats them, so
      the picker looks broken at mobile before anything is even clicked.

      Seeking by source index rather than by pixel offset is what makes this safe: the stride is
      measured fresh each time, so the same call is correct at any width.
    */
    let selected = (() => {
      const shown = kids(panel)[positionOf(panel)];
      return shown && !Number.isNaN(srcOf(shown)) ? srcOf(shown) : srcOf(labels[0]);
    })();

    /*
      Bring the chosen name into view, but only when it is not already there. The strip is a
      clipped scroller: at desktop all six names fit and paging it would shove the first ones out
      of sight for no reason, while at mobile only two fit, so choosing one from the far end left
      the pill sitting off-screen with nothing to show which name was selected.
    */
    const port = (() => {
      let n = strip.parentElement;
      while (n) { if (/hidden|clip|auto|scroll/.test(getComputedStyle(n).overflowX)) return n; n = n.parentElement; }
      return null;
    })();
    const reveal = () => {
      if (!port) return;
      const chip = labels.find((el) => srcOf(el) === selected);
      if (!chip) return;
      const a = chip.getBoundingClientRect();
      const b = port.getBoundingClientRect();
      if (a.left < b.left + 1 || a.right > b.right - 1) seek(strip, selected);
    };

    const apply = () => { followers.forEach((t) => seek(t, selected)); paint(selected); };
    const activate = (src) => { selected = src; apply(); reveal(); };
    apply();

    let raf = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        followers.forEach((t) => { t.style.transitionDuration = '0ms'; });
        apply();
      });
    });

    labels.forEach((slide) => {
      slide.addEventListener('click', () => activate(srcOf(slide)));
    });

    /*
      The arrows either side of the strip belong to the strip's own carousel, so the generic pass
      above wired them to scroll the NAMES — which left the selection, the panel and the impact
      row untouched and made the arrows feel broken in a different way. They should step the
      SELECTION, and let the strip follow only if the new name is out of view.

      Replacing each button with a clone is what drops the generic listener; there is no handle on
      it to remove. Clearing `disabled` matters too: the generic pass had disabled `previous` at
      index 0, and a disabled <button> never fires a click.
    */
    const order = [...new Set(labels.map(srcOf))].sort((a, b) => a - b);
    [...section.querySelectorAll('[data-replo-part="nav-button"][data-direction]')]
      .filter((btn) => ownerTrack(btn) === strip)
      .forEach((btn) => {
        const fresh = btn.cloneNode(true);
        btn.replaceWith(fresh);
        fresh.removeAttribute('disabled');
        fresh.setAttribute('aria-disabled', 'false');
        const step = fresh.getAttribute('data-direction') === 'previous' ? -1 : 1;
        fresh.addEventListener('click', (e) => {
          e.preventDefault();
          const i = order.indexOf(selected);
          activate(order[(i + step + order.length) % order.length]);
        });
      });

    linked++;
  });

  if (linked) {
    console.info('[replo-baked-carousel] linked %d name strip(s) to their panels', linked);
  }

  // A resize changes the step, so each track has to be re-seated or it drifts out of alignment
  // with its dots.
  if ('ResizeObserver' in window && groups.size) {
    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return; }
      groups.forEach((g, track) => {
        const slides = [...track.children].filter((el) => el.matches('[data-replo-part="slide"]'));
        if (slides.length < 2) return;
        const idx = Math.max(0, g.dots.findIndex((d) => d.getAttribute('aria-current') === 'true'));
        const stride = slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
        track.style.transitionDuration = '0ms';
        track.style.transform = `translate3d(${-idx * stride}px, 0px, 0px)`;
      });
    });
    ro.observe(document.documentElement);
  }
})();
